import React, { useState } from 'react';
import { Code2, Megaphone, ShieldCheck, TrendingUp, Sparkles, Layers, ArrowRight } from 'lucide-react';
import { Card, Badge } from '../ui';

export const TechnookGrowthEngineHub: React.FC<{ onOpenConsultation?: () => void }> = ({ onOpenConsultation }) => {
  const [activeEngine, setActiveEngine] = useState<'tech' | 'growth'>('tech');

  return (
    <div className="relative w-full max-w-xl mx-auto">
      {/* Background ambient decorative glows */}
      <div className="absolute -top-8 -right-8 w-72 h-72 bg-[#08adae]/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-8 -left-8 w-72 h-72 bg-[#4a82c2]/20 rounded-full blur-3xl pointer-events-none" />

      {/* Main Technook Hybrid Engine Glass Container */}
      <Card variant="glass" className="relative z-10 p-6 sm:p-8 bg-white/95 dark:bg-[#0f1b2d]/95 border border-slate-200/90 dark:border-[#4a82c2]/30 backdrop-blur-2xl shadow-2xl shadow-blue-950/10 dark:shadow-black/40">
        
        {/* Header Badge & Engine Switcher */}
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800/80 pb-4 mb-6">
          <div className="flex items-center gap-2">
            <Badge variant="accent" className="text-xs uppercase tracking-wider">
              <Layers size={13} /> Dual Synergy Hub
            </Badge>
            <span className="text-xs font-bold text-[#184166] dark:text-white font-mono">
              Tech + Growth Engine
            </span>
          </div>
          <span className="text-[11px] font-semibold text-sky-600 dark:text-sky-400 bg-sky-500/10 border border-sky-500/30 px-2.5 py-0.5 rounded-full">
            Synergy Index: 98/100
          </span>
        </div>

        {/* Dual Tab Switcher Buttons */}
        <div className="grid grid-cols-2 gap-2 p-1.5 bg-slate-100 dark:bg-[#09101d] rounded-2xl border border-slate-200 dark:border-slate-800 mb-6">
          <button
            onClick={() => setActiveEngine('tech')}
            className={`py-2.5 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
              activeEngine === 'tech'
                ? 'bg-white dark:bg-[#182a45] text-[#4a82c2] dark:text-white shadow-md border border-[#4a82c2]/30'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <Code2 size={16} className="text-[#4a82c2]" />
            <span>Software Engineering</span>
          </button>
          <button
            onClick={() => setActiveEngine('growth')}
            className={`py-2.5 px-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 ${
              activeEngine === 'growth'
                ? 'bg-white dark:bg-[#182a45] text-[#08adae] dark:text-white shadow-md border border-[#08adae]/30'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            <Megaphone size={16} className="text-[#08adae]" />
            <span>Marketing & Sales</span>
          </button>
        </div>

        {/* Tab 1 Content: Software Engineering Core */}
        {activeEngine === 'tech' ? (
          <div className="space-y-4 animate-in fade-in duration-300">
            <div className="p-4 rounded-2xl bg-sky-50/80 dark:bg-[#0b1322]/80 border border-[#4a82c2]/30 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#184166] dark:text-white flex items-center gap-1.5">
                  <ShieldCheck size={16} className="text-emerald-500" /> Enterprise Code Architecture
                </span>
                <span className="text-[10px] font-mono font-semibold text-[#4a82c2]">99.9% SLA</span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                Custom Web Portals, Cross-Platform iOS & Android Apps, Cloud Microservices, and Automated QA Systems engineered from Parañaque City.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 rounded-xl bg-white dark:bg-[#09101d] border border-slate-200 dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400 block text-[10px] uppercase font-semibold">Tech Stack</span>
                <span className="font-bold text-[#184166] dark:text-slate-200">React, TypeScript, AWS</span>
              </div>
              <div className="p-3 rounded-xl bg-white dark:bg-[#09101d] border border-slate-200 dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400 block text-[10px] uppercase font-semibold">Delivery SLA</span>
                <span className="font-bold text-emerald-600 dark:text-emerald-400">Zero Debt Production</span>
              </div>
            </div>
          </div>
        ) : (
          /* Tab 2 Content: Marketing & Growth Engine */
          <div className="space-y-4 animate-in fade-in duration-300">
            <div className="p-4 rounded-2xl bg-teal-50/80 dark:bg-[#0b1322]/80 border border-[#08adae]/30 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-[#184166] dark:text-white flex items-center gap-1.5">
                  <TrendingUp size={16} className="text-[#08adae]" /> Brand Expansion & Pipeline
                </span>
                <span className="text-[10px] font-mono font-semibold text-[#08adae]">3.4x Brand Lift</span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                High-converting 3D Architectural Visuals, Motion Video Assets, B2B Outbound Prospecting, and Executive Appointment Setting.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-3 rounded-xl bg-white dark:bg-[#09101d] border border-slate-200 dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400 block text-[10px] uppercase font-semibold">B2B Appointments</span>
                <span className="font-bold text-[#184166] dark:text-slate-200">140+ Qualified Meetings</span>
              </div>
              <div className="p-3 rounded-xl bg-white dark:bg-[#09101d] border border-slate-200 dark:border-slate-800">
                <span className="text-slate-500 dark:text-slate-400 block text-[10px] uppercase font-semibold">Asset Formats</span>
                <span className="font-bold text-[#08adae]">3D Render, Video, Ads</span>
              </div>
            </div>
          </div>
        )}

        {/* Footer Action */}
        <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800/80 flex items-center justify-between">
          <span className="text-xs font-semibold text-[#184166] dark:text-slate-300 flex items-center gap-1">
            <Sparkles size={15} className="text-[#08adae]" /> Tech + Growth Integration
          </span>
          <button
            onClick={onOpenConsultation}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4a82c2] to-[#184166] hover:opacity-95 text-white text-xs font-bold shadow-md shadow-[#4a82c2]/20 flex items-center gap-1.5 transition-all"
          >
            <span>Discuss Hybrid Scope</span> <ArrowRight size={13} />
          </button>
        </div>

      </Card>
    </div>
  );
};
